import { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useRepositories } from '../../../infrastructure/http/RepositoryProvider';
import type { AiSite } from '../../../domain/Site';
import type { AiConversation, FieldAnalysis } from '../../../domain/Ai';

// El backend responde 503 con este mismo texto cuando AI_ENABLED=false (ver
// aiController.ts) — lo usamos para distinguir "IA apagada" de un error real.
// Administrar conversaciones no pasa por ese gate, solo las acciones que
// llaman al modelo (acciones rápidas, mandar un mensaje).
const DISABLED_MESSAGE = 'El asistente de IA no está disponible en este momento.';

export type QuickReply = { kind: 'text'; text: string } | { kind: 'analysis'; analysis: FieldAnalysis };

export function useAiConversations() {
  const history = useHistory();
  const { ai: aiRepo } = useRepositories();

  const [sites, setSites] = useState<AiSite[]>([]);
  const [selectedSiteId, setSelectedSiteId] = useState<string | null>(null);
  const [loadingSites, setLoadingSites] = useState(true);

  const [conversations, setConversations] = useState<AiConversation[]>([]);
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [aiDisabled, setAiDisabled] = useState(false);
  const [quickLoading, setQuickLoading] = useState(false);
  const [quickReply, setQuickReply] = useState<QuickReply | null>(null);

  const [sitesError, setSitesError] = useState<string | null>(null);

  const loadSites = (showSpinner: boolean) => {
    if (showSpinner) setLoadingSites(true);
    setSitesError(null);
    aiRepo
      .listSites()
      .then(({ sites: list }) => {
        setSites(list);
        setSelectedSiteId((current) => (current && list.some((s) => s.id === current) ? current : list[0]?.id ?? null));
      })
      .catch((err) => setSitesError(err instanceof Error ? err.message : 'No se pudieron cargar los campos.'))
      .finally(() => setLoadingSites(false));
  };

  useEffect(() => {
    loadSites(true);
  }, [aiRepo]);

  const loadConversations = (siteId: string) => {
    setLoadingConversations(true);
    setError(null);
    aiRepo
      .listConversations(siteId)
      .then(({ conversations: list }) => setConversations(list))
      .catch((err) => setError(err instanceof Error ? err.message : 'No se pudieron cargar las conversaciones.'))
      .finally(() => setLoadingConversations(false));
  };

  useEffect(() => {
    if (selectedSiteId) loadConversations(selectedSiteId);
  }, [selectedSiteId]);

  // Ionic mantiene esta página viva en el stack de navegación al entrar a
  // una conversación y volver atrás (no la remonta), así que el useEffect de
  // arriba no vuelve a disparar solo con eso. useIonViewWillEnter tampoco
  // sirve acá de forma confiable. Lo que sí es 100% consistente es
  // escuchar los cambios de ruta del history de react-router directo: cada
  // vez que la URL vuelve a coincidir con /app/ai, recargamos la lista.
  const selectedSiteIdRef = useRef(selectedSiteId);
  selectedSiteIdRef.current = selectedSiteId;

  useEffect(() => {
    return history.listen((location) => {
      if (location.pathname !== '/app/ai') return;
      // Los campos también: si se creó uno mientras esta página seguía viva
      // en el stack, tiene que aparecer sin recargar la app.
      loadSites(false);
      if (selectedSiteIdRef.current) loadConversations(selectedSiteIdRef.current);
    });
  }, [history]);

  // Crea una conversación nueva y navega al hilo. Si se pasa `firstQuestion`
  // (botones de acceso rápido tipo "¿Cómo está mi campo?"), el hilo la manda
  // sola apenas se abre (ver useAiConversation + location.state).
  async function startConversation(firstQuestion?: string) {
    if (!selectedSiteId) return;
    try {
      const { conversation } = await aiRepo.createConversation(selectedSiteId);
      history.push(`/app/ai/${conversation.id}`, firstQuestion ? { firstQuestion } : undefined);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo crear la conversación.');
    }
  }

  async function deleteConversation(id: string) {
    try {
      await aiRepo.deleteConversation(id);
      setConversations((prev) => prev.filter((c) => c.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo borrar la conversación.');
    }
  }

  async function runQuickAction(action: () => Promise<QuickReply>) {
    if (!selectedSiteId) return;
    setQuickLoading(true);
    setError(null);
    setQuickReply(null);
    try {
      setQuickReply(await action());
    } catch (err) {
      const message = err instanceof Error ? err.message : 'No pudimos responder tu consulta.';
      if (message === DISABLED_MESSAGE) setAiDisabled(true);
      else setError(message);
    } finally {
      setQuickLoading(false);
    }
  }

  const analyzeField = () =>
    runQuickAction(async () => ({ kind: 'analysis', analysis: await aiRepo.analyzeField(selectedSiteId as string) }));

  const dailySummary = () =>
    runQuickAction(async () => {
      const { summary } = await aiRepo.getDailySummary(selectedSiteId as string);
      return { kind: 'text', text: summary };
    });

  return {
    sites,
    selectedSiteId,
    setSelectedSiteId,
    loadingSites,
    sitesError,
    retrySites: () => loadSites(true),
    conversations,
    loadingConversations,
    error,
    aiDisabled,
    quickLoading,
    quickReply,
    startConversation,
    deleteConversation,
    analyzeField,
    dailySummary,
  };
}
